import React from 'react';
import { cn } from '@/lib/utils';
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, Pencil } from 'lucide-react';

/**
 * The stock list as it reads on a phone.
 *
 * The desktop grid is eight columns wide. At 375px that is a horizontal scroll
 * bar with a table attached, and the two things the store keeper opened the app
 * for — in and out — sit off the right edge of it, three swipes away from the
 * name of the item they belong to.
 *
 * A card puts the name, the number and those two buttons inside one thumb's
 * reach. Cost and value only appear when the store keeper is allowed to see
 * money; the server strips those fields for the roles that are not.
 */

const num = (v) => Number(v || 0).toLocaleString();

function StockCard({ item, low, selected, onSelect, onStockIn, onStockOut, onEdit }) {
  const value = (item.current_stock || 0) * (item.cost_per_unit || 0);
  const negative = item.current_stock < 0;

  return (
    <li
      data-testid={`stock-card-${item.id}`}
      className={cn(
        'overflow-hidden rounded-lg border bg-white shadow-card',
        negative || low ? 'border-l-4 border-slate-200 border-l-red-500' : 'border-slate-200',
        selected && 'ring-2 ring-brand-blue/40'
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex w-full items-start gap-3 px-3 pb-2 pt-2.5 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold leading-tight text-slate-900">
            {item.name}
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500">
            {item.category}
            {item.location ? ` · ${item.location}` : ''}
          </span>
        </span>

        <span className="shrink-0 text-right">
          <span
            className={cn(
              'block text-2xl font-bold leading-none tabular-nums',
              negative ? 'text-red-600' : low ? 'text-amber-600' : 'text-slate-900'
            )}
          >
            {num(item.current_stock)}
          </span>
          <span className="mt-0.5 block text-[11px] uppercase tracking-wider text-slate-400">
            {item.unit}
          </span>
        </span>
      </button>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 pb-2 text-[11px] text-slate-500">
        {item.minimum_stock > 0 && (
          <span className={low ? 'font-semibold text-amber-700' : undefined}>
            {low && <AlertTriangle className="mr-0.5 inline h-3 w-3 align-[-2px]" />}
            min {num(item.minimum_stock)}
          </span>
        )}
        {item.cost_per_unit > 0 && <span>₹{num(item.cost_per_unit)}/{item.unit}</span>}
        {value > 0 && <span>value ₹{num(value)}</span>}
      </div>

      <div className="grid grid-cols-[1fr_1fr_auto] gap-px border-t border-slate-100 bg-slate-100">
        <button
          type="button"
          onClick={onStockIn}
          data-testid={`stock-in-${item.id}`}
          className="flex h-12 items-center justify-center gap-1.5 bg-white text-sm font-semibold text-emerald-700 active:bg-emerald-50"
        >
          <ArrowUpCircle className="h-4 w-4" />
          Stock In
        </button>
        <button
          type="button"
          onClick={onStockOut}
          data-testid={`stock-out-${item.id}`}
          className="flex h-12 items-center justify-center gap-1.5 bg-white text-sm font-semibold text-red-600 active:bg-red-50"
        >
          <ArrowDownCircle className="h-4 w-4" />
          Stock Out
        </button>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${item.name}`}
          data-testid={`edit-item-${item.id}`}
          className="flex h-12 w-12 items-center justify-center bg-white text-slate-400 active:bg-slate-50"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

export default function StockCards({
  items, isLow, selectedId, onSelect, onStockIn, onStockOut, onEdit,
}) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white py-12 text-sm text-slate-400 shadow-card">
        <AlertTriangle className="h-8 w-8 text-slate-300" />
        No items found
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-2" data-testid="stock-cards">
      {items.map((item) => (
        <StockCard
          key={item.id}
          item={item}
          low={isLow(item)}
          selected={selectedId === item.id}
          onSelect={() => onSelect(item)}
          onStockIn={() => onStockIn(item)}
          onStockOut={() => onStockOut(item)}
          onEdit={() => onEdit(item)}
        />
      ))}
    </ul>
  );
}
